import { useEffect, useRef } from 'react'
import { listTasks } from '../api/tasks'
import { listComplianceItems } from '../api/compliance'
import { listCalendarEvents } from '../api/calendar'
import { listDeals } from '../api/deals'
import { listContacts } from '../api/contacts'
import { listProspects } from '../api/prospects'
import { listClients } from '../api/clients'
import { checkDashboardNotifications } from '../utils/dashboardNotifications'
import { getNotificationPreferences } from '../api/notifications'
import type { Task, ComplianceItem, CalendarEvent, Deal, Contact, Prospect, Client } from '../types'
import { processLoadResults } from '../utils/apiLoadTracker'

type _NotificationEnvelope = {
  title: string
  body: string
  url: string
  tag: string
}

// Check interval: 15 minutes (reduced from 5 minutes to improve performance)
// Background polling with visibility check to save resources when tab is hidden
const CHECK_INTERVAL = 15 * 60 * 1000

export function useRealtimeNotifications(userId?: string | null, agencyId?: string | null) {
  const lastCheckRef = useRef<number>(0)

  useEffect(() => {
    if (!userId || !agencyId) return

    // Capture values as constants so TypeScript knows they're strings inside the async function
    const currentUserId = userId
    const currentAgencyId = agencyId

    // Function to check for notifications
    async function checkNotifications() {
      // Skip polling if tab is hidden (saves network and battery)
      if (document.hidden) {
        return
      }

      try {
        // Load all data needed for notification checks
        const results = await Promise.allSettled([
          listTasks(),
          listComplianceItems(),
          listCalendarEvents(),
          listDeals(),
          listContacts(),
          listProspects(),
          // Clients feed the milestone checks (birthday, contract expiry,
          // draft anniversary). They were never passed before Sep 2026, so
          // those notifications could not fire.
          listClients(),
        ])
        const loadedData = processLoadResults(
          results,
          ['Tasks', 'Compliance', 'Calendar', 'Deals', 'Contacts', 'Prospects', 'Clients'],
          false // Don't show toast for background notifications
        )
        const tasks = loadedData[0] as Task[]
        const complianceItems = loadedData[1] as ComplianceItem[]
        const events = loadedData[2] as CalendarEvent[]
        const deals = loadedData[3] as Deal[]
        const contacts = loadedData[4] as Contact[]
        const prospects = loadedData[5] as Prospect[]
        const clients = loadedData[6] as Client[]

        // Load user preferences for configurable thresholds
        const preferences = await getNotificationPreferences(currentUserId)

        // Check and create notifications
        await checkDashboardNotifications(currentUserId, currentAgencyId, {
          tasks,
          complianceItems,
          events,
          deals,
          contacts,
          prospects,
          clients,
        }, preferences)

        lastCheckRef.current = Date.now()
      } catch (err) {
        console.warn('Failed to check notifications:', err)
      }
    }

    // Initial check on mount (but only if we haven't checked recently)
    const timeSinceLastCheck = Date.now() - lastCheckRef.current
    if (timeSinceLastCheck > CHECK_INTERVAL) {
      // Delay initial check by 5 seconds to let app settle
      const initialTimeout = setTimeout(checkNotifications, 5000)

      // Set up periodic checks
      const interval = setInterval(checkNotifications, CHECK_INTERVAL)

      return () => {
        clearTimeout(initialTimeout)
        clearInterval(interval)
      }
    } else {
      // Just set up periodic checks from now
      const interval = setInterval(checkNotifications, CHECK_INTERVAL)
      return () => clearInterval(interval)
    }
  }, [userId, agencyId])
}
